/* LicenseRestrictionAddon.js */

"use strict";

const EventEmitter = require("events").EventEmitter;

const NO_LICENSE_ERROR_CODE = "NoValidLicense";
const BEFORE_LICENSE_ERROR_CODE = "BeforeActiveLicense";
const EXPIRED_LICENSE_ERROR_CODE = "LicenseExpired";
const unavailableCodes = [NO_LICENSE_ERROR_CODE, BEFORE_LICENSE_ERROR_CODE, EXPIRED_LICENSE_ERROR_CODE];

/**
 * LicenseRestrictionAddon module.
 * This module load and stores current org lisences.
 * Adapted to work in ModelEditor
 *
 * How to use this addon:
 * 1. Get the working module instance with di (no need to create new instances!)
 * 2. Make sure module is ready with licenseRestriction.whenLicenseFeedIsReady()
 * 3. Check the license with licenseRestriction.checkLicense(licenseNameToCheck)
 * 4. All current license data is stored in the _licenseData, use licenseRestriction.licenseData to access to it
 *
 */

class LicenseRestrictionAddOn extends EventEmitter {

    constructor(licenseStateEntity, userSession, steveDi, solutionsManager, modalUserFeedback, logger) {
        super();
        this._logger = logger;
        this._modalUserFeedback = modalUserFeedback;
        this._userSession = userSession;
        this._solutionsManager = solutionsManager;
        this._di = steveDi;
        this._controlAppNav = null;
        this._currentLicenseFeed = undefined;

        this._orgId = null;
        this._isReady = false;

        this._isGluwareLicenseAvailable = false;

        /**
         * @type {Object <LicenseCodeName, LicenseState>}
         * @private
         */
        this._licenseData = {};

        if (licenseStateEntity) {
            this._retrieveLicenseStateData = this._prepareLicenseStateFeed(licenseStateEntity);
        }

        this._onOrganizationChange = this._onOrganizationChange.bind(this);
        this._onActiveSolutionChanged = this._onActiveSolutionChanged.bind(this);

        this._onFeedInitialized = (data, feed /*, reInit*/) => {
            this._onFeedUpdates(feed.getCacheAsArray());
        };
        this._onFeedEntityAdd = (entity, properties, feed) => {
            this._onFeedUpdates(feed.getCacheAsArray());
        };
        this._onFeedEntityRemoved = (entity, cause, feed) => {
            this._onFeedUpdates(feed.getCacheAsArray());
        };
        this._onFeedEntityUpdated = (entity, patches, props, feed) => {
            this._onFeedUpdates(feed.getCacheAsArray());
        };

        this._registerListeners();
    }

    get controlAppNav() {
        if (this._controlAppNav) {
            return this._controlAppNav;
        }

        const controlAppNavRegistered = this._di.getRegistered("$teve.controlAppNav");
        const controlAppNav = controlAppNavRegistered && controlAppNavRegistered.registeredObj;
        this._controlAppNav = controlAppNav;
        return controlAppNav;
    }

    get licenseData() {
        return this._licenseData;
    }

    /**
     * This function should only be called when license data is uploaded in the UI.
     * @param orgId
     */
    reloadLicenseData(orgId) {
        this._retrieveLicenseStateData(orgId);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerDataUpdated(data, previousData) {
        this.emit("Data", data, previousData);
    }
    _registerListeners() {
        this._userSession.on("OrganizationChange", this._onOrganizationChange);
        this._solutionsManager.on("activeSolutionChanged", this._onActiveSolutionChanged);
    }
    _registerFeedListeners(feed) {
        feed.once("ShutdownStarted", this._unregisterFeedListeners.bind(this, feed));
        feed.on("ChangeFeedInitialized", this._onFeedInitialized);
        feed.on("EntityAdded", this._onFeedEntityAdd);
        feed.on("EntityRemoved", this._onFeedEntityRemoved);
        feed.on("EntityPropertiesUpdated", this._onFeedEntityUpdated);
    }
    _unregisterListeners() {
        this._userSession.removeListener("OrganizationChange", this._onOrganizationChange);
        this._solutionsManager.removeListener("activeSolutionChanged", this._onActiveSolutionChanged);
    }

    _unregisterFeedListeners(feed) {
        feed.removeListener("ChangeFeedInitialized", this._onFeedInitialized);
        feed.removeListener("EntityAdded", this._onFeedEntityAdd);
        feed.removeListener("EntityRemoved", this._onFeedEntityRemoved);
        feed.removeListener("EntityPropertiesUpdated", this._onFeedEntityUpdated);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     *
     * @param {LicenseStateEntity[]} LicenseStateEntities
     * @private
     */
    _onFeedUpdates(LicenseStateEntities) {
        const parsedData = LicenseStateEntities.map((LicenseStateEntity) => LicenseStateEntity.getProperties()).reduce(
            (dataObj, stateProps) => Object.assign(dataObj, { [stateProps.name]: stateProps }),
            {}
        );
        this._setLicenseStateData(parsedData);
    }

    _onOrganizationChange({id}) {
        if (this._orgId === id) {
            return;
        }
        this._isReady = false;
        this._currentLicenseFeed = undefined; // clearing currentFeed reference since a new ascend call needs to happen
        this._orgId = id;

        this._retrieveLicenseStateData(id);

        this._checkSolution(this._solutionsManager.activeSolution);
    }

    _onActiveSolutionChanged(activeSolution) {
        this._checkSolution(activeSolution);
    }

    _onServiceData({ activeSolutions = {}, inactiveSolutions = {} }) {
        //assume that values in inactiveSolutions object is a solution errors
        inactiveSolutions = Object.entries(inactiveSolutions).reduce(
            (acc, [solutionName, error]) => Object.assign(acc, { [solutionName]: { error } }),
            {}
        );
        const data = Object.assign({}, activeSolutions, inactiveSolutions);
        this._setLicenseStateData(data);
        return Promise.resolve();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _prepareLicenseStateFeed(LicenseStateEntity) {
        const self = this;
        const query = {};
        const fields = {uniqueProp: false};
        let currentFeed = null;
        self.once("Shutdown", () => {
            if (currentFeed) {
                currentFeed.shutdown();
                currentFeed = null;
            }
        });

        return (orgId) => {
            if (!orgId) {
                return;
            }
            this._isReady = false;
            if (currentFeed) {
                currentFeed.shutdown();
            }
            currentFeed = LicenseStateEntity.startAscendLicenseStatesFeed(orgId, query, fields);
            this._currentLicenseFeed = currentFeed; // setting currentfeed at instance level
            // Letting the below code run to set ready value when initialized,
            // reduces having to call whenInitialized later
            currentFeed.whenInitialized()
                .then(() => {
                    this._isReady = true;
                })
                .catch((err) => {
                    this._isReady = false;
                    this._logger.warn({error: err}, "LicenseRestrictionAddOn._prepareLicenseStateFeed() - Unable to startLicenseStateFeed");
                });
            self._registerFeedListeners(currentFeed);
            return currentFeed;
        };
    }

    _setLicenseStateData(data) {
        const previousData = this._licenseData;
        this._licenseData = data;
        this._triggerDataUpdated(this._licenseData, previousData);
    }

    _checkSolution(solution) {
        // If solution is null then create solution object with name Dashboard and License "GLUWARE"
        // This is to redirect to settings section, licensing if no license available
        // Case - 24196
        // This redirects any solution without license to settings
        if(this._userSession.isConnectedAndAuthenticated && !this._userSession.isConnectedAndAuthenticated()) {
            this._isGluwareLicenseAvailable = false;
            return;
        }
        const routeDetails = this._solutionsManager._router.getRouteDetails();
        if (routeDetails.s === "PasswordReset") {
            return;
        }
        if(!solution || (typeof solution === "object" && Object.entries(solution) < 1)) {
            solution = {
                name: "Dashboard",
                license: ["GLUWARE"]
            };
        }
        if (solution && solution.license) {
            this._modalUserFeedback.displayLoading()
            .then(() => this.whenLicenseFeedIsReady())
            .then(() => {
                this._modalUserFeedback.hideDisplayed();
                const isLicenseAvailable = this.checkLicense(solution.license);
                if (!isLicenseAvailable) {
                    if(!this._isGluwareLicenseAvailable) {
                        this._goToLicense();
                    } else {
                        this._goToDashboard();
                    }
                }
            });
        }
    }

    _goToLicense() {
        this.controlAppNav.routeToLicensing();
    }
    _goToDashboard() {
        this.controlAppNav.routeToDashboard();
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /**
     * Used in ME to create License requests
     * @param {Object} LicenseService
     */
    setLicenseService(LicenseService) {
        if (!LicenseService) {
            this._logger.warn({error: {}}, "LicenseRestrictionAddOn.setLicenseService() - LicenseService is not provided");
            return;
        }

        this._retrieveLicenseStateData = (orgId) => {
            this._isReady = false;

            this._activeSolutionsRequest = LicenseService.getActiveSolutions({ orgId: orgId })
                .then((solutions) => {
                    return this._onServiceData(solutions);
                })
                .then(() => {
                    this._isReady = true;
                    return Promise.resolve();
                })
                .catch((err) => {
                    this._logger.warn({error: err}, "LicenseRestrictionAddOn.setLicenseService() - Unable to create _activeSolutionsRequest");
                    this._isReady = false;
                });

            return this._activeSolutionsRequest;
        };

        if (this._orgId) {
            this._retrieveLicenseStateData(this._orgId);
        }
    }

    checkLicense(types = []) {
        types = [].concat(types);
        return types.every(this.isLicenseAvailable.bind(this));
    }

    isLicenseAvailable(type) {
        const licenseState = this._licenseData[type];
        let isAvailable = true;
        if (!licenseState) {
            return false;
        }
        isAvailable = this.licenseIsActive(licenseState.error);
        if(type === "GLUWARE" && isAvailable && !this._isGluwareLicenseAvailable) {
            this._isGluwareLicenseAvailable = true;
        }
        return isAvailable;
    }

    shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();
        this.removeAllListeners();

        this._logger = undefined;
        this._isReady = undefined;
        this._orgId = undefined;
        this._modalUserFeedback = undefined;
        this._userSession = undefined;
        this._solutionsManager = undefined;
        this._di = undefined;
        this._controlAppNav = undefined;
        this._licenseData = undefined;
        this._currentLicenseFeed = undefined;
        this._isGluwareLicenseAvailable = undefined;

        return Promise.resolve();
    }

    async whenLicenseFeedIsReady() {  // jshint ignore:line
        if (this._isReady === true) {
            return Promise.resolve();
        } else if(this._currentLicenseFeed) {
            // for Game center, instead of waiting for _isReady using sleep, returning whenInitialized promise
            return this._currentLicenseFeed.whenInitialized();
        }
        else {
            // ME doesnt work with the feed and so running the timer if _isReady is false
            for (let msWaited = 0; msWaited < 15000; msWaited+=500) {
                await sleep(500); // jshint ignore:line
                if (this._isReady === true) {
                    return Promise.resolve();
                }
            }

            return Promise.reject("Can't establish license restriction feed, timeout");
        }

        function sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

    }
    /*******************************************/
    /* END PUBLIC API METHODS                */
    /*******************************************/

    /*
    static createIsReadyPromise() {
        let resolver;
        let rejecter;
        const promise = new Promise((resolve, reject) => {
            resolver = resolve;
            rejecter = reject;
        });
        return {
            promise,
            resolve: () => resolver(),
            reject: (error) => rejecter(error),
            reset: () => {
                rejecter({cancelled: true});
                return LicenseRestrictionAddOn.createIsReadyPromise();
            }
        };
    }*/

    //consider to refactor it!
    licenseIsActive(licenseStateError) {
        const { errorCode } = this.parseError(licenseStateError);
        return unavailableCodes.indexOf(errorCode) < 0;
    }

    parseError(errorString) {
        const errorMatches = String(errorString).match(/(\w+) - (.+)/) || [];
        return {
            errorCode: errorMatches[1],
            errorMsg: errorMatches[2]
        };
    }
}

LicenseRestrictionAddOn.$inject = [
    "$teve.entities.LicenseState",
    "$teve.userSession",
    "$teve.di",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
    "$teve.logger",
];
module.exports = LicenseRestrictionAddOn;
